import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import * as _ from 'lodash';
import { switchMap, take, takeUntil } from 'rxjs';
import { UserService } from 'src/app/core/services/user.service';
import { BaseComponent } from './../../../core/components/base/base.component';
import { FileObject } from './../../../core/interfaces/file-object';
import { FeedbackService } from './../../../core/services/feedback.service';
import { GroupService } from './../../../core/services/group.service';
import { SupportService } from './../../../core/services/support.service';
import { HospitalService } from './../../../core/services/hospital.service';
import { MedicationService } from './../../../core/services/medication.service';
import { MedicationAutocompleteComponent } from './../../../shared/components/medication-autocomplete/medication-autocomplete.component';
import { LabTestSelectorComponent } from './../../../shared/components/lab-test-selector/lab-test-selector.component';
import { ChangePasswordDialogComponent } from './change-password-dialog/change-password-dialog.component';
import { EditProfileDialogComponent } from './edit-profile-dialog/edit-profile-dialog.component';
//import { TutorialService } from 'src/app/core/tutorial/tutorial.service';
import { PdfDownloadService } from 'src/app/core/services/pdf-download.service';
import * as moment from 'moment';
import { NgxMaterialTimepickerTheme } from 'ngx-material-timepicker';
import { ThemeService } from 'src/app/core/services/theme.service';
import { environment } from 'src/environments/environment';
import { envFlag } from 'src/app/shared/util/env-flag.util';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';

@Component({
  selector: 'app-profile-settings',
  templateUrl: './profile-settings.component.html',
  styleUrls: ['./profile-settings.component.scss']
})
export class ProfileSettingsComponent extends BaseComponent implements OnInit, OnDestroy {
  userProfile: any;
  userRole?: 'Admin' | 'Physician' | 'Support-Limited' | 'Support-Unlimited';
  groupProfile: any;

  @ViewChild('groupSettingsDialog') groupSettingsDialog?: TemplateRef<any>;
  @ViewChild('securitySettingsDialog') securitySettingsDialog?: TemplateRef<any>;

  /**
   * §7 rule 8 shadow-mode status for the ICD-10 section of the settings dialog.
   *
   * Explanatory only. The server refuses the setting (409), so a stale or unfetched status
   * here cannot arm anything — which is why `permitted` starts effectively false.
   */
  icdAutoAcceptStatus?: { permitted: boolean; reason: string; measuredAddPrecision: number | null; decisionsRecorded: number };
  icdAutoAcceptStatusLoading = false;

  groupUsers: any[] = [];
  invitingUser: any = {};
  @ViewChild('inviteUserDialog') inviteUserDialog?: TemplateRef<any>;

  hospitals: any[] = [];
  addingHospital: any = {};
  deletedWardIds: string[] = [];
  @ViewChild('addHospitalDialog') addHospitalDialog?: TemplateRef<any>;

  medicationBundles: any[] = [];
  addingMedicationBundle: any = {};
  @ViewChild('addMedicationBundleDialog') addMedicationBundleDialog?: TemplateRef<any>;
  @ViewChild('medicationList') medicationList?: MedicationAutocompleteComponent;


  @ViewChild('preferredMedicationDialog') preferredMedicationDialog?: TemplateRef<any>;
  @ViewChild('preferredMedicationList') preferredMedicationList?: MedicationAutocompleteComponent;

  @ViewChild('labTestBundleDialog') labTestBundleDialog?: TemplateRef<any>;
  @ViewChild('labTestBundleList') labTestBundleList?: LabTestSelectorComponent;

  headerImageObjects: any[] = [];
  footerImageObjects: any[] = [];

  canSignOnDoctorsBehalf: boolean = false;

  /** Patient app settings (linking, patient-facing hours, after-hours reply) show only when the build enables the app. */
  readonly patientAppFeatureEnabled = envFlag(environment, 'patientAppEnabled');

  // Lab test bundle properties
  labTestBundles: any[] = [];
  editingLabTestBundle: any = {};
  isLoading = false;
  openingWhatsAppSupport = false;

  // Auto-save properties
  private isAutoSaving = false;
  timepickerTheme!: NgxMaterialTimepickerTheme;

  /** A state unit (tasks/state-mode.md): no patient app, no invites, no lab orders, no ICD-10 review pipeline. */
  get stateMode(): boolean {
    return isStateModeGroup(this.groupService.group);
  }

  constructor(
    private userService: UserService,
    private groupService: GroupService,
    private supportService: SupportService,
    private hospitalService: HospitalService,
    private medicationService: MedicationService,
    private pdfDownloadService: PdfDownloadService,
    private themeService: ThemeService,
    //public tutorialService: TutorialService,

    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);

    // The practice-hours pickers follow the app theme, like every other ngx-material-timepicker.
    this.themeService.isDarkMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isDarkMode) => (this.timepickerTheme = this.themeService.getTimepickerTheme(isDarkMode)));

    this.userService.userProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        if (user) {
          this.userProfile = user;
          this.initializeLabTestBundles();
        }
      });

    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe((group) => {
        if (group) {
          this.groupProfile = group;
          // Initialize enforcedInviteSections if it doesn't exist
          if (!this.groupProfile.enforcedInviteSections) {
            this.groupProfile.enforcedInviteSections = { nextOfKin: false, noPrivatePatients: false };
          }
          // Initialize autoGenerateSections if it doesn't exist
          if (!this.groupProfile.autoGenerateSections) {
            this.groupProfile.autoGenerateSections = { dischargeSummary: false };
          }
          if (this.groupProfile.calendarSplitByPhysician === undefined || this.groupProfile.calendarSplitByPhysician === null) {
            this.groupProfile.calendarSplitByPhysician = false;
          }
          this.fetchGroupUsers();
          if (group.headerImageUrl) {
            this.headerImageObjects = [{ url: group.headerImageUrl, type: 'image' }];
          }
          if (group.footerImageUrl) {
            this.footerImageObjects = [{ url: group.footerImageUrl, type: 'image' }];
          }
        }
      });

    this.hospitalService.hospitals$
      .pipe(takeUntil(this.destroy$))
      .subscribe(hospitals => {
        if (hospitals) {
          this.hospitals = hospitals;
        }
      });

    this.medicationService.bundles$
      .pipe(takeUntil(this.destroy$))
      .subscribe(bundles => {
        if (bundles) {
          this.medicationBundles = bundles;
        }
      });
  }

  ngOnInit() {
    // Show the tutorial when this component is initialized
    this.fetchGroupUsers();
  }

  isAdmin() {
    return this.userRole?.toLowerCase() === 'admin';
  }

  isDoctor() {
    return this.userService.user?.userType.toLowerCase() === 'doctor';
  }

  isGroupUser() {
    if (!this.userRole) return false;
    return !this.isAdmin();
  }

  openEditProfileDialog(event?: MouseEvent) {
    event?.preventDefault();
    event?.stopPropagation();


    let dialogRef = this.dialog.open(EditProfileDialogComponent, {
      data: { ...this.userProfile }
    });

    // The dialog now performs the save itself and only closes once the server has accepted it.
    // It has to: the Lancet delivery preference can be REFUSED (not a member of that practice,
    // another account already claimed delivery), and a dialog that has already closed has
    // nowhere to put the reason and no field left to put the rejected value back into. On
    // failure it stays open with specific copy; on success it closes with `{ saved: true }`,
    // and `{ saved: false }` means the user changed nothing, so there is nothing to announce.
    dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
      if (result?.saved) {
        this.showToastMessage('Profile updated successfully');
      }
    })
  }

  /** Structured hours for the patient app (tasks/patient-app.md §4.5); the server validates (utils/practiceHours.js). */
  readonly practiceDays = [
    { key: 'mon', label: 'Monday' }, { key: 'tue', label: 'Tuesday' }, { key: 'wed', label: 'Wednesday' },
    { key: 'thu', label: 'Thursday' }, { key: 'fri', label: 'Friday' }, { key: 'sat', label: 'Saturday' },
    { key: 'sun', label: 'Sunday' },
  ];

  setStructuredHours(on: boolean): void {
    if (!on) {
      this.groupProfile.practiceHours = null;
      return;
    }
    const weekday = () => [{ from: '08:00', to: '17:00' }];
    this.groupProfile.practiceHours = {
      timeZone: 'Africa/Johannesburg',
      week: { mon: weekday(), tue: weekday(), wed: weekday(), thu: weekday(), fri: weekday(), sat: [], sun: [] },
      publicHolidaysClosed: true,
    };
  }

  /**
   * What PATIENTS are shown (tasks/patient-app.md §4.5): the structured hours win wherever they are set, so the
   * practice can see the sentence its patients read. Mirrors `utils/practiceHours.js describePracticeHours`.
   */
  get patientHoursText(): string | null {
    const hours = this.groupProfile?.practiceHours;
    if (!hours?.week) return null;
    const ranges = (day: string) => (hours.week[day] || []).map((r: { from: string; to: string }) => `${r.from}–${r.to}`).join(', ');
    if (this.practiceDays.every((d) => !ranges(d.key))) return null;
    const runs: { days: string[]; text: string }[] = [];
    for (const day of this.practiceDays) {
      const text = ranges(day.key);
      const last = runs[runs.length - 1];
      if (last && last.text === text) last.days.push(day.label.slice(0, 3));
      else runs.push({ days: [day.label.slice(0, 3)], text });
    }
    const label = (run: { days: string[] }) => (run.days.length === 1 ? run.days[0] : `${run.days[0]}–${run.days[run.days.length - 1]}`);
    const parts = runs.filter((r) => r.text).map((r) => `${label(r)} ${r.text}`);
    const closed = runs.filter((r) => !r.text).map(label);
    if (closed.length) parts.push(`closed ${closed.join(' and ')}`);
    return hours.publicHolidaysClosed ? `${parts.join(', ')}; closed on public holidays` : parts.join(', ');
  }

  /** A time in the free text that the patient-app hours do not mention: the two are telling patients different things. */
  get hoursDisagree(): boolean {
    const shown = this.patientHoursText;
    const typed = String(this.groupProfile?.practiceOpeningTimes || '');
    if (!shown || !typed.trim()) return false;
    const times = typed.match(/\d{1,2}[:h][0-5]\d/g) || [];
    if (!times.length) return false;
    return times.some((t) => !shown.includes(t.replace('h', ':').padStart(5, '0')));
  }

  addHoursRange(day: string): void {
    const week = this.groupProfile.practiceHours?.week;
    if (!week) return;
    const ranges = week[day] || (week[day] = []);
    const last = ranges[ranges.length - 1];
    ranges.push(last ? { from: last.to, to: last.to < '17:00' ? '17:00' : '24:00' } : { from: '08:00', to: '17:00' });
  }

  openEditGroupProfileDialog(event?: MouseEvent) {
    event?.preventDefault();
    event?.stopPropagation();

    const dialogRef = this.dialog.open(this.groupSettingsDialog!);

    // The ICD-10 section explains whether unattended application is available yet, so the
    // status has to be in hand while the dialog is open rather than only after a toggle.
    if (this.groupProfile?.icdSuggestionsEnabled) {
      this.fetchIcdAutoAcceptStatus();
    }

    this.fetchSuggestedInternalDomains();

    const sub = dialogRef.afterClosed().subscribe(updatedGroup => {
      if (updatedGroup) {
        // Sent as the comma-separated string the column stores. Sent even when empty, because an
        // admin removing the last domain is a deliberate act — the server reads `!== undefined`,
        // so omitting it would leave the old value in place and quietly keep the practice opted in.
        updatedGroup.internalEmailDomains = this.confirmedInternalDomains.join(',');

        const updateSub = this.groupService.updateGroupSettings({ id: this.groupProfile.id, ...updatedGroup })
          .subscribe(
            (response) => {
              if (response.success) {
                this.showToastMessage('Profile updated successfully');
              }
              updateSub.unsubscribe();
              sub.unsubscribe();
            },
            (error) => {
              console.error('Error updating profile:', error);
              // A refused ICD auto-accept request carries a specific, actionable reason from
              // the server (§7 rule 8). Replacing it with "please try again" would tell the
              // practice to retry something that cannot succeed yet.
              const refusal = error?.error?.code === 'ICD_AUTO_ACCEPT_NOT_PERMITTED' ? error.error.message : null;
              // A rejected domain names itself and says why. "Please try again" would send an
              // admin round the same loop, and they would reasonably conclude it had saved.
              const domainRefusal = error?.error?.code === 'INVALID_INTERNAL_EMAIL_DOMAINS' ? error.error.message : null;
              if (domainRefusal) {
                const rejected = (error.error.details?.rejected || []).map((r: any) => r.domain);
                this.confirmedInternalDomains = this.confirmedInternalDomains.filter(d => !rejected.includes(d));
                this.showToastMessage(domainRefusal);
              } else if (refusal) {
                this.groupProfile.autoAcceptIcdSuggestions = false;
                this.showToastMessage(refusal);
              } else {
                this.showToastMessage('There was an error updating your profile. Please try again.');
              }
              updateSub.unsubscribe();
              sub.unsubscribe();
            });
      }
    })
  }

  /**
   * Master switch for the ICD-10 coding assistant (§10).
   *
   * Mirrors the server's cascade in `groupController.updateGroupSettings`: auto-accept is a
   * second, narrower consent that cannot outlive the feature switch. Clearing it here as well
   * keeps the dialog honest about what will be saved — the server would clear it anyway, and a
   * toggle that stays visibly on while being turned off underneath is worse than no toggle.
   */
  onIcdSuggestionsEnabledChange(checked: boolean) {
    if (!this.groupProfile) return;

    if (!checked) {
      this.groupProfile.autoAcceptIcdSuggestions = false;
      return;
    }
    this.fetchIcdAutoAcceptStatus();
  }

  private fetchIcdAutoAcceptStatus() {
    if (!this.groupProfile?.id) return;

    this.icdAutoAcceptStatusLoading = true;
    const statusSub = this.groupService.getIcdAutoAcceptStatus(this.groupProfile.id)
      .subscribe(
        (response) => {
          this.icdAutoAcceptStatus = response?.data;
          this.icdAutoAcceptStatusLoading = false;
          statusSub.unsubscribe();
        },
        (error) => {
          console.error('Error fetching ICD auto-accept status:', error);
          // Fails closed: an unreadable measurement must not read as "available".
          this.icdAutoAcceptStatus = {
            permitted: false,
            reason: 'the accuracy measurement for this practice could not be read',
            measuredAddPrecision: null,
            decisionsRecorded: 0,
          };
          this.icdAutoAcceptStatusLoading = false;
          statusSub.unsubscribe();
        });
  }

  // ── Practice email domains ────────────────────────────────────────────────────────────────
  //
  // Confirming a domain means clinical letters to that domain are sent as PLAIN, unencrypted
  // PDFs. Nothing is confirmed by default and nothing is derived automatically — the server
  // reads only what an admin has ticked here.
  suggestedInternalDomains: string[] = [];
  confirmedInternalDomains: string[] = [];
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];

  private fetchSuggestedInternalDomains() {
    if (!this.groupProfile?.id) return;

    const sub = this.groupService.getSuggestedInternalDomains(this.groupProfile.id)
      .subscribe(
        (response) => {
          this.suggestedInternalDomains = response?.data?.suggested || [];
          this.confirmedInternalDomains = response?.data?.confirmed || [];
          sub.unsubscribe();
        },
        (error) => {
          console.error('Error fetching suggested practice domains:', error);
          // Fails EMPTY, never open. An admin who sees no suggestions types the domain by hand;
          // an invented one would be confirmed by someone who assumed we knew.
          this.suggestedInternalDomains = [];
          sub.unsubscribe();
        });
  }

  /** Suggestions the practice has not already confirmed. */
  get unconfirmedSuggestions(): string[] {
    return this.suggestedInternalDomains.filter(d => !this.confirmedInternalDomains.includes(d));
  }

  /** From the chip input. */
  addInternalDomain(event: MatChipInputEvent): void {
    this.addInternalDomainValue(event.value);
    event.chipInput.clear();
  }

  /** From a suggestion chip, or from the input above. */
  addInternalDomainValue(domain: string): void {
    // Accepts a whole address as well as a bare domain — an admin pasting "me@x.co.za" means the
    // same thing and should not have to know that. The server normalises again and is the
    // authority on what is allowed.
    const cleaned = (domain || '').trim().toLowerCase().replace(/^.*@/, '');
    if (!cleaned || this.confirmedInternalDomains.includes(cleaned)) return;
    this.confirmedInternalDomains = [...this.confirmedInternalDomains, cleaned];
  }

  removeInternalDomain(domain: string) {
    this.confirmedInternalDomains = this.confirmedInternalDomains.filter(d => d !== domain);
  }

  openEditSecuritySettingsDialog(event?: MouseEvent) {
    event?.preventDefault();
    event?.stopPropagation();

    const dialogRef = this.dialog.open(this.securitySettingsDialog!);

    dialogRef.afterClosed().subscribe(updated => {
      if (updated) {
        if (updated.autoLogoutTime < 5 || updated.autoLogoutTime > 180) {
          this.showToastMessage('Auto Logout Time must be a value between 5 minutes and 180 minutes');
          return;
        }
        this.userService.updateUserProfile({ id: this.userProfile.id, autoLogoutTime: updated.autoLogoutTime })
          .subscribe(
            (response) => {
              if (response.success) {
                this.showToastMessage('Security settings updated successfully');
              }
            },
            (error) => {
              console.error('Error updating security settings:', error);
              this.showToastMessage('There was an error updating your security settings. Please try again.');
            });
      }
    })
  }

  openChangePasswordDialog() {
    let dialogRef = this.dialog.open(ChangePasswordDialogComponent, {
    });

    dialogRef.afterClosed().subscribe(updatedUser => {
      if (updatedUser) {
        this.userService.updateUserProfile({ id: this.userProfile.id, ...updatedUser })
          .subscribe(
            (response) => {
              if (response.success) {
                this.showToastMessage('Profile updated successfully');
              }
            },
            (error) => {
              console.error('Error updating profile:', error);
              this.showToastMessage('There was an error updating your profile. Please try again.');
            });
      }
    })
  }

  openPreferredMedicationDialog(event?: MouseEvent) {
    event?.preventDefault();
    event?.stopPropagation();

    const dialogRef = this.dialog.open(this.preferredMedicationDialog!, { width: '600px' });

    dialogRef.afterClosed().subscribe(updates => {
      if (updates) {
        this.userProfile.preferredMedication = updates;
        this.userService.updateUserProfile({ preferredMedication: this.userProfile.preferredMedication }).pipe(
          take(1)
        ).subscribe({
          next: (response) => {
            if (response.success) {
              this.showToastMessage('Preferred medication updated successfully');
            }
          },
          error: (error) => {
            console.error('Error updating preferred medication settings:', error);
            this.showToastMessage('There was an error updating your preferred medication settings. Please try again.');
          }
        });
      }
    })
  }

  private initializeLabTestBundles() {
    // Initialize lab test bundles from user profile
    this.labTestBundles = this.userProfile.preferredLabTests || [];
  }


  private generateUniqueId(): string {
    // Generate a unique ID using timestamp and random number
    return `bundle_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private cleanBundleData(bundle: any): any {
    // Remove unwanted fields and ensure proper structure
    const cleanBundle = {
      id: bundle.id,
      label: bundle.label,
      lab: bundle.lab || 'Lancet',
      labTests: bundle.labTests || []
    };

    // Clean lab tests to preserve essential fields including isMicro and meta
    cleanBundle.labTests = cleanBundle.labTests.map((test: any) => {
      const cleanTest: any = {
        orderCode: test.orderCode,
        lancetPrintNumber: test.lancetPrintNumber,
        mnemonic: test.mnemonic,
        name: test.name,
        type: test.type,
        defaultLoinc: test.defaultLoinc,
        isNumeric: test.isNumeric,
      };

      // Preserve isMicro field if it exists
      if (test.isMicro !== undefined) {
        cleanTest.isMicro = test.isMicro;
      }

      // Preserve meta object if it exists (including specimenSource)
      if (test.meta) {
        cleanTest.meta = test.meta;
      } else if (test.isMicro) {
        // If isMicro is true but meta doesn't exist, initialize it
        cleanTest.meta = {};
      }

      return cleanTest;
    });

    return cleanBundle;
  }


  private autoSaveLabTestBundles() {
    // Prevent multiple simultaneous saves
    if (this.isAutoSaving) {
      return;
    }

    this.isAutoSaving = true;

    // Clean bundles before saving to ensure isMicro and meta are preserved
    const cleanedBundles = this.labTestBundles.map(bundle => this.cleanBundleData(bundle));

    // Update user profile with current lab test bundles
    this.userProfile.preferredLabTests = cleanedBundles;

    // Save to backend immediately like coding bundles
    // The updateUserProfile method already updates the observable via tap()
    this.userService.updateUserProfile({ preferredLabTests: this.userProfile.preferredLabTests }).pipe(
      take(1)
    ).subscribe({
      next: (response) => {
        this.isAutoSaving = false;
        if (response.success) {
          console.log('Lab test bundle auto-saved successfully');
          // Observable is already updated by updateUserProfile's tap() operator
        }
      },
      error: (error) => {
        this.isAutoSaving = false;
        console.error('Error auto-saving lab test bundle:', error);
        this.showToastMessage('There was an error saving your changes. Please try again.');
      }
    });
  }

  openAddLabTestBundleDialog(event: any, bundle?: any) {
    event?.preventDefault();
    event?.stopPropagation();

    this.editingLabTestBundle = bundle ? { ...bundle } : {
      id: this.generateUniqueId(),
      label: '',
      labTests: [],
      lab: 'Lancet' // Default lab
    };

    const dialogRef = this.dialog.open(this.labTestBundleDialog!, {
      width: '800px',
      maxHeight: '90vh'
    });

    dialogRef.afterClosed().subscribe(result => {
      // Dialog was closed, no need to do anything here as we handle saving in the button click
    });
  }

  onLabTestBundleTestsChanged(tests: any[], bundle?: any) {
    // Clean the tests data before assigning, preserving isMicro and meta
    const cleanTests = tests.map((test: any) => {
      const cleanTest: any = {
        orderCode: test.orderCode,
        lancetPrintNumber: test.lancetPrintNumber,
        mnemonic: test.mnemonic,
        name: test.name,
        type: test.type,
        defaultLoinc: test.defaultLoinc,
        isNumeric: test.isNumeric,
        similarity: test.similarity
      };

      // Preserve isMicro field if it exists
      if (test.isMicro !== undefined) {
        cleanTest.isMicro = test.isMicro;
      }

      // Preserve meta object if it exists (including specimenSource)
      if (test.meta) {
        cleanTest.meta = test.meta;
      } else if (test.isMicro) {
        // If isMicro is true but meta doesn't exist, initialize it
        cleanTest.meta = {};
      }

      return cleanTest;
    });

    if (bundle) {
      bundle.labTests = cleanTests; // Direct assignment like coding bundles

      // Only auto-save if not already saving to prevent loops
      if (!this.isAutoSaving) {
        this.autoSaveLabTestBundles(); // Immediate save like coding bundles
      }
    } else {
      this.editingLabTestBundle.labTests = [...cleanTests]; // Create new array reference
    }
  }

  saveLabTestBundle() {
    if (!this.editingLabTestBundle.label || !this.editingLabTestBundle.labTests?.length) {
      this.showToastMessage('Please provide a bundle name and at least one lab test.');
      return;
    }

    this.isLoading = true;

    // Clean the bundle data before saving (preserves isMicro and meta)
    const cleanBundle = this.cleanBundleData(this.editingLabTestBundle);

    const index = this.labTestBundles.findIndex((bundle: any) => bundle.id === cleanBundle.id);

    if (index !== -1) {
      // Update existing bundle
      this.labTestBundles[index] = cleanBundle;
    } else {
      // Add new bundle
      this.labTestBundles.push(cleanBundle);
    }

    // Clean all bundles before saving to ensure consistency
    const cleanedBundles = this.labTestBundles.map(bundle => this.cleanBundleData(bundle));

    // Update user profile
    this.userProfile.preferredLabTests = cleanedBundles;

    // Save to backend
    // The updateUserProfile method already updates the observable via tap()
    this.userService.updateUserProfile({ preferredLabTests: this.userProfile.preferredLabTests }).pipe(
      take(1)
    ).subscribe({
      next: (response) => {
        if (response.success) {
          this.showToastMessage('Lab test bundle updated successfully');
          this.dialog.closeAll();
          // Observable is already updated by updateUserProfile's tap() operator
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error updating lab test bundle:', error);
        this.showToastMessage('There was an error updating your lab test bundle. Please try again.');
        this.isLoading = false;
      }
    });
  }

  closeLabTestBundleDialog() {
    this.dialog.closeAll();
  }

  removePreferredMedication(med: any) {
    this.userProfile.preferredMedication = this.userProfile.preferredMedication.filter((pm: any) => pm.id !== med.id);

    this.userService.updateUserProfile({ preferredMedication: this.userProfile.preferredMedication }).pipe(
      take(1)
    ).subscribe({
      next: (response) => {
        if (response.success) {
          this.showToastMessage('Preferred medication updated successfully');
        }
      },
      error: (error) => {
        console.error('Error updating preferred medication settings:', error);
        this.showToastMessage('There was an error updating your preferred medication settings. Please try again.');
      }
    });
  }

  removeLabTestFromBundle(test: any, bundle: any) {
    bundle.labTests = bundle.labTests.filter((t: any) => t.id !== test.id);

    // Clean bundles before saving to ensure isMicro and meta are preserved
    const cleanedBundles = this.labTestBundles.map(b => this.cleanBundleData(b));

    // Update user profile
    this.userProfile.preferredLabTests = cleanedBundles;

    // Save to backend
    // The updateUserProfile method already updates the observable via tap()
    this.userService.updateUserProfile({ preferredLabTests: this.userProfile.preferredLabTests }).pipe(
      take(1)
    ).subscribe({
      next: (response) => {
        if (response.success) {
          this.showToastMessage('Lab test removed successfully');
          // Observable is already updated by updateUserProfile's tap() operator
        }
      },
      error: (error) => {
        console.error('Error removing lab test:', error);
        this.showToastMessage('There was an error removing the lab test. Please try again.');
      }
    });
  }

  deleteLabTestBundle(bundleId: any) {
    this.labTestBundles = this.labTestBundles.filter((bundle: any) => bundle.id !== bundleId);

    // Clean bundles before saving to ensure isMicro and meta are preserved
    const cleanedBundles = this.labTestBundles.map(b => this.cleanBundleData(b));

    // Update user profile
    this.userProfile.preferredLabTests = cleanedBundles;

    // Save to backend
    // The updateUserProfile method already updates the observable via tap()
    this.userService.updateUserProfile({ preferredLabTests: this.userProfile.preferredLabTests }).pipe(
      take(1)
    ).subscribe({
      next: (response) => {
        // If we get here, the request succeeded (no error was thrown)
        // Check for success flag or user object to confirm
        if (response?.success || response?.user) {
          this.showToastMessage('Lab test bundle deleted successfully');
        } else {
          // Even if response structure is unexpected, if we got here, it likely succeeded
          // The observable was already updated optimistically
          this.showToastMessage('Lab test bundle deleted successfully');
        }
      },
      error: (error) => {
        console.error('Error deleting lab test bundle:', error);
        // Revert the local change if the save failed
        this.labTestBundles = this.userProfile.preferredLabTests || [];
        this.showToastMessage('There was an error deleting the lab test bundle. Please try again.');
      }
    });
  }


  toggleGeneratePatientNumbers(event: MatSlideToggleChange) {
    const generatePatientNumbers = event.checked;
    this.groupService.updateGroupSettings({ id: this.groupProfile.id, generatePatientNumbers })
      .subscribe(
        (response) => {
          if (response.success) {
            this.showToastMessage('Patient file settings updated successfully');
          }
        },
        (error) => {
          console.error('Error updating patient file settings:', error);
          this.showToastMessage('There was an error updating your patient file settings. Please try again.');
        });
  }

  openAddUserDialog(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.invitingUser = { id: this.groupUsers.length + 1, canSeeFinancials: false, canSignOnDoctorsBehalf: false, canDownloadRecordings: true };
    this.dialog.open(this.inviteUserDialog!);
  }

  getTooltipText(user: any): string {
    return `Email: ${user.email}, User Role: ${user.userRole}`;
  }

  editUser(user: any): void {
    if (user.isPending) return; // Ensure the user is not pending before opening the dialog
    this.invitingUser = { ...user, isEdit: true };
    this.dialog.open(this.inviteUserDialog!);
  }

  fetchGroupUsers() {
    this.groupService.getGroupUsers()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (results) => {
          const currentGroupUser = results.users?.filter((u: any) => u.userId === this.userProfile.id);
          this.userRole = currentGroupUser[0].userRole;

          this.groupUsers = _.chain(results.users)
            .sortBy(u => u.userRole)
            .map((user: any) => {
              return {
                ...user,
                isOwner: user.userId === this.groupProfile.ownerId,
                isPending: !user.userId  // Determine if the user is a pending invite
              };
            })
            .value();
        },
        (error) => {
          console.error('Error fetching group users:', error);
        }
      );
  }

  isDuplicateGroupUser() {
    const invite = this.invitingUser; // Retrieve the invite form values
    const users = this.groupUsers; // Retrieve the current group users

    // Case-insensitive: the server stores invite addresses lowercased and trimmed, and accepts
    // them against the signed-in account the same way, so the duplicate check must match that.
    const inviteEmail = (invite.email || '').trim().toLowerCase();
    const existingUser = users.find(u => (u.email || '').trim().toLowerCase() === inviteEmail); // Check if a user with the same email exists

    if (existingUser) {
      this.showConfirmationDialog(
        'DUPLICATE FOUND',
        'A user with the same email address already exists in this group and cannot be added',
        undefined,
        'OK',
        ''
      );
      return true; // Indicate that a duplicate user was found
    }

    return false; // No duplicate found
  }

  onInviteSubmit() {
    const { id, name, email, userRole, isEdit, canSignOnDoctorsBehalf, canSeeFinancials, canDownloadRecordings } = this.invitingUser;

    if (isEdit) {
      this.updateUserRole(id, userRole, canSignOnDoctorsBehalf, canSeeFinancials, canDownloadRecordings);
      return
    }

    if (name && email && userRole) {
      if (!this.isDuplicateGroupUser()) {
        this.groupService.inviteUser(email.trim().toLowerCase(), userRole, name, canSignOnDoctorsBehalf, canSeeFinancials, canDownloadRecordings)
          .pipe(takeUntil(this.destroy$))
          .subscribe(
            () => {
              this.showToastMessage('User invited successfully');
              this.fetchGroupUsers();
              this.dialog.closeAll();
            },
            (error) => {
              this.showToastMessage('An error has occured sending the invite. Please try again.');
              console.error('Error inviting user:', error);
            }
          );
      }
    } else {
      this.showToastMessage('Name, Email, and User Role are required when sending an invite. Please try again.');
    }
  }

  requestSupport() {
    this.groupService.inviteUser('contact@medicai.co.za', 'Admin', 'MedicAI Support', true, true, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.showToastMessage('Support request submitted successfully.');
          this.fetchGroupUsers();
        },
        (error) => {
          this.showToastMessage('An error has occured submitting the support request. Please try again.');
          console.error('Error submitting support request:', error);
        }
      );
  }

  openWhatsAppSupport(): void {
    if (!this.userProfile || !this.groupProfile || this.openingWhatsAppSupport) {
      return;
    }
    this.openingWhatsAppSupport = true;
    this.supportService.openWhatsAppSupport(this.userProfile, this.groupProfile)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.openingWhatsAppSupport = false;
          this.fetchGroupUsers();
        },
        error: () => {
          this.openingWhatsAppSupport = false;
          this.showToastMessage('Failed to open WhatsApp support. Please try again.');
        }
      });
  }

  async removeUser(user: any) {
    const confirmed = await this.showConfirmationDialog();
    if (!confirmed) return;

    if (user.isPending) {
      // Remove the pending invite
      this.groupService.removeInvite(user.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          () => {
            this.showToastMessage('Pending invite removed successfully');
            this.fetchGroupUsers();
          },
          (error) => {
            this.showToastMessage('An error has occurred removing the pending invite. Please try again.');
            console.error('Error removing invite:', error);
          }
        );
    } else {
      // Remove the group user
      this.groupService.removeUser(user.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          () => {
            this.showToastMessage('User removed successfully');
            this.fetchGroupUsers();
          },
          (error) => {
            this.showToastMessage('An error has occurred removing the group user. Please try again.');
            console.error('Error removing user:', error);
          }
        );
    }
  }

  updateUserRole(userId: string, newRole: string, canSignOnDoctorsBehalf: boolean, canSeeFinancials?: boolean, canDownloadRecordings?: boolean) {
    this.groupService.updateUserRole(userId, newRole, canSignOnDoctorsBehalf, canSeeFinancials, canDownloadRecordings)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          const index = this.groupUsers.findIndex(u => u.id === userId);
          this.groupUsers[index] = { ...this.groupUsers[index], userRole: newRole, canSignOnDoctorsBehalf: canSignOnDoctorsBehalf, canSeeFinancials: canSeeFinancials, canDownloadRecordings: canDownloadRecordings };
          this.showToastMessage('User role updated successfully');
          this.dialog.closeAll();
        },
        (error) => {
          console.error('Failed to update user role:', error);
          this.showToastMessage('Failed to update user role. Please try again.');
        }
      );
  }

  openAddHospitalDialog(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.deletedWardIds = [];
    this.addingHospital = { id: this.hospitals.length + 1, wards: [] };
    this.dialog.open(this.addHospitalDialog!, { width: '600px' });
  }

  openAddMedicationBundleDialog(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.addingMedicationBundle = { id: this.medicationBundles.length + 1, medication: [], userId: this.userService.user.id, confirmSubstitutions: false };
    this.dialog.open(this.addMedicationBundleDialog!, { width: '600px' });
  }


  getHospitalTooltipText(hospital: any): string {
    return `Name: ${hospital.name}, Wards: ${hospital.wards.map((w: any) => w.name).join(', ')}`;
  }

  editHospital(hospital: any): void {
    this.deletedWardIds = [];
    this.addingHospital = { ...hospital, isEdit: true };
    this.dialog.open(this.addHospitalDialog!);
  }

  editMedicationBundle(bundle: any): void {
    this.addingMedicationBundle = { ...bundle, isEdit: true };
    this.dialog.open(this.addMedicationBundleDialog!);
  }

  async removeHospital(hospital: any) {
    const confirmed = await this.showConfirmationDialog();
    if (!confirmed) return;
    // Remove the hospital
    this.hospitals = this.hospitals.filter(h => h.id !== hospital.id);
    this.hospitalService.updateHospitals(this.hospitals, [hospital.id], [])
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.showToastMessage('Hospital removed successfully');

        },
        (error) => {
          this.showToastMessage('An error has occurred removing the hospital. Please try again.');
          console.error('Error removing hospital:', error);
        }
      );
  }

  async removeMedicationBundle(bundle: any) {
    const confirmed = await this.showConfirmationDialog();
    if (!confirmed) return;
    this.medicationBundles = this.medicationBundles.filter(item => item.id !== bundle.id);
    this.medicationService.updateMedicationBundles(this.medicationBundles)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.showToastMessage('Medication bundle removed successfully');

        },
        (error) => {
          this.showToastMessage('An error has occurred removing the medication bundle. Please try again.');
          console.error('Error removing medication bundle:', error);
        }
      );
  }

  trackWardDeletion(wardId: string) {
    if (wardId) {
      this.deletedWardIds.push(wardId);
    }
  }

  saveHospital() {
    const { name, wards } = this.addingHospital;

    if (name) {

      if (!wards?.length) {
        this.showToastMessage('Wards are required when adding a hospital. Please add a ward to continue.')
        return;
      }

      this.hospitalService.updateHospitals([this.addingHospital], [], this.deletedWardIds)
        .subscribe(
          (response) => {
            if (response) {
              this.showToastMessage('Hospitals saved successfully');
              this.dialog.closeAll();
            }
          },
          (error) => {
            console.error('Error updating hospitals:', error);
            this.showToastMessage('There was an error updating hospitals. Please try again.')
          });
    } else {
      this.showToastMessage('Hospital Name is required when saving a hospital. Please try again.');
    }
  }

  saveMedicationBundle() {
    this.addingMedicationBundle.medication = this.medicationList!.getMedicationList();
    const { label, medication } = this.addingMedicationBundle;

    if (label) {

      if (!medication?.length) {
        this.showToastMessage('Medication is required when adding a medication bundle. Please add at least on medication to continue.')
        return;
      }

      this.medicationBundles.push(this.addingMedicationBundle)

      this.medicationService.updateMedicationBundles(this.medicationBundles)
        .subscribe(
          (response) => {
            if (response) {
              this.showToastMessage('Medication Bundles saved successfully');
              this.dialog.closeAll();
            }
          },
          (error) => {
            console.error('Error updating medication bundles:', error);
            this.showToastMessage('There was an error updating medication bundles. Please try again.')
          });
    } else {
      this.showToastMessage('Bundle name is required when saving a medication bundle. Please try again.');
    }
  }

  onHeaderImageUpload(files: FileObject[]) {
    if (files.length > 0) {
      this.headerImageObjects = [{
        ...files[0],
        //url: URL.createObjectURL(files[0]), 
        type: 'image'
      }];
      this.saveLetterheadSettings();
    }
  }

  onFooterImageUpload(files: FileObject[]) {
    if (files.length > 0) {
      this.footerImageObjects = [{
        ...files[0],
        ///url: URL.createObjectURL(files[0]), 
        type: 'image'
      }];
      this.saveLetterheadSettings();
    }
  }

  onImageDelete(file: FileObject, imageType: 'header' | 'footer') {
    this.deleteImageFromServer(imageType);
  }

  private deleteImageFromServer(imageType: 'header' | 'footer') {
    this.groupService.deleteGroupLetterHeadImage(imageType)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          if (imageType === 'header') {
            this.headerImageObjects = [];
          }
          if (imageType === 'footer') {
            this.footerImageObjects = [];
          }
          const capitalizedImageType = imageType.charAt(0).toUpperCase() + imageType.slice(1);
          this.feedbackService.showToast(`${capitalizedImageType} image deleted successfully.`);
        },
        error: (error) => {
          this.feedbackService.showToast(`There was an error deleting the ${imageType} image. Please try again.`);
        }
      });
  }

  async saveLetterheadSettings() {
    const formData = new FormData();
    if (this.headerImageObjects.length > 0 && this.headerImageObjects[0].url.indexOf('medicaisastorage') === -1) {
      formData.append('headerImage', await this.blobToFile(this.headerImageObjects[0].url, 'header.png'));
    }
    if (this.footerImageObjects.length > 0 && this.footerImageObjects[0].url.indexOf('medicaisastorage') === -1) {
      formData.append('footerImage', await this.blobToFile(this.footerImageObjects[0].url, 'footer.png'));
    }
    this.groupService.updateGroupLetterHead(formData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.feedbackService.showToast('Letterhead settings updated successfully.');
        },
        error: (error) => {
          this.feedbackService.showToast('There was an error updating your letterhead settings. Please try again.');
        }
      });
  }

  async blobToFile(url: string, defaultFileName: string) {
    const response = await fetch(url);
    const blob = await response.blob();
    const contentType = blob.type;

    let extension = '';
    if (contentType === 'image/png') {
      extension = '.png';
    } else if (contentType === 'image/jpeg' || contentType === 'image/jpg') {
      extension = '.jpg';
    } else {
      throw new Error('Unsupported image format: ' + contentType);
    }

    const fileName = defaultFileName.replace(/\.[^/.]+$/, extension); // Replace the default extension with the correct one
    return new File([blob], fileName, { type: contentType });
  }

  async leaveGroup() {
    const confirmed = await this.showConfirmationDialog('Leave Group', 'Are you sure you want to leave this group?');
    if (confirmed) {
      this.groupService.removeUser(this.userProfile.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result) => {
            this.showToastMessage('You have successfully left the group');
            this.userService.getUserProfile()
              .pipe(
                takeUntil(this.destroy$),
                switchMap(() => this.groupService.getUserGroups(this.userProfile.id, this.userProfile.userType)),
              ).subscribe();
          },
          error: (error) => {
            console.error('Error leaving group: ', error);
            this.showToastMessage('There was an error leaving the group. Please try again.');
          }
        });
    }
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
  }

  previewLetterhead() {
    const filename = `MedicAI_Document_Preview_${moment().valueOf()}`;
    const content = '';
    this.pdfDownloadService.downloadPdf({
      content: content,
      filename: filename,
      patient: { name: 'Sample', surname: 'Patient', dob: moment().subtract(25, 'years') },
      images: [],
    });
  }

  /* restartTutorial() {
    this.tutorialService.resetTutorialToDefaults()
      .subscribe({
        next: () => {
          console.log("Tutoral progress reset succesfully.")
        },
        error: (err: Error) => {
          console.error('Error resetting tutorial', err);
        }
      })
  } */

}
